type Props = {
  language?: "en" | "zh";
};

/**
 * Privacy Policy tab content (English + Simplified Chinese), replicated from the
 * icheck-chinese project. Re-mounting on `language` (via `key`) replays the
 * slide/fade enter animation using tailwindcss-animate utilities — no
 * framer-motion dependency.
 */
const PrivacyPolicyTab = ({ language = "en" }: Props) => {
  return (
    <div
      key={language}
      className="animate-in fade-in slide-in-from-right-10 duration-500 ease-in-out"
    >
      <div className="privacy-content">
        {language === "zh" ? <ChineseContent /> : <EnglishContent />}
      </div>
    </div>
  );
};

const EnglishContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>
    <div className="rounded-lg border border-border bg-background/40 p-5 sm:p-6 mb-6">
      <h2 className="text-lg sm:text-xl font-semibold mb-4">Key Information</h2>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 sm:gap-y-3 text-sm sm:text-base">
        <dt className="font-medium">App Name:</dt>
        <dd>iCheck - Inspection Hub</dd>

        <dt className="font-medium">Developer Name:</dt>
        <dd>Anuj Mengaji</dd>

        <dt className="font-medium">Contact Email:</dt>
        <dd>
          <a href="mailto:support@elementree.co.in" className="text-primary underline">
            support@elementree.co.in
          </a>
        </dd>

        <dt className="font-medium">Effective Date:</dt>
        <dd>14 May 2026</dd>

        <dt className="font-medium">Last Updated:</dt>
        <dd>14 May 2026</dd>
      </dl>
    </div>
    <p className="mb-4">
      Thank you for using iCheck - Inspection Hub, a compliance and inspection
      management system provided by Anuj Mengaji. We (“iCheck,” “we,” “us”)
      respect your privacy and want you to understand how we collect,
      use, and share data about you. This Privacy Policy covers our data
      collection practices and describes your rights regarding your personal
      data.
    </p>
    <p className="mb-4">
      Unless we link to a different policy or state otherwise, this Privacy
      Policy applies when you visit or use the iCheck websites, mobile
      applications, APIs, or related services (the “Services”).
    </p>
    <p className="mb-4">
      By using the Services, you agree to the terms of this Privacy Policy.
      You shouldn’t use the Services if you don’t agree with this Privacy
      Policy or any other agreement that governs your use of the Services.
    </p>

    <h2 className="text-xl font-semibold mb-2">Table of Contents</h2>
    <ul className=" pl-6 mb-4">
      <li>
        <a href="#whatData">1. What Data We Get</a>
      </li>
      <li>
        <a href="#howWe">2. How We Get Data About You</a>
      </li>
      <li>
        <a href="#weUse">3. What We Use Your Data For</a>
      </li>
      <li>
        <a href="#weShare">4. Who We Share Your Data With</a>
      </li>
      <li>
        <a href="#security">5. Security</a>
      </li>
      <li>
        <a href="#yourRights">6. Your Rights</a>
      </li>
      <li>
        <a href="#jurisdiction">7. Jurisdiction-Specific Rules</a>
      </li>
      <li>
        <a href="#updates">8. Updates & Contact Info</a>
      </li>
    </ul>

    <h2 id="whatData" className="text-xl font-semibold">
      1. What Data We Get
    </h2>
    <p className="mb-4">
      We collect certain data from you directly, like information you enter
      yourself, inspection data you submit, and information from third-party
      platforms you connect with iCheck. We also collect some data
      automatically, like information about your device and what parts of
      our Services you interact with or spend time using.
    </p>

    <h3 className="text-lg font-semibold mb-2">
      1.1 Data You Provide to Us
    </h3>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>Account Data:</strong> To create a user account, we collect
        and store your email address, password, and account settings. As you
        use certain features on the platform, you may be prompted to submit
        additional information including your name, occupation, and phone
        number.
      </li>
      <li>
        <strong>Profile Data:</strong> You can also choose to provide
        profile information like a photo, designation, biography, language,
        or other details. Your Profile Data may be viewable by your company
        administrators or relevant stakeholders.
      </li>
      <li>
        <strong>Inspection & Compliance Data:</strong> When you record
        inspection results, attach evidence, or submit compliance reports,
        we collect and store that data as part of your account history.
      </li>
      <li>
        <strong>Payment Data:</strong> If you subscribe or make purchases,
        we collect billing details as necessary to process your order.
      </li>
      <li>
        <strong>Communications and Support:</strong> If you contact us for
        support or to report a problem, we collect and store your contact
        information, messages, and other details about you.
      </li>
    </ul>

    <h3 className="text-lg font-semibold mb-2">
      1.2 Data We Collect through Automated Means
    </h3>
    <p>
      When you access the Services, we collect certain data by automated
      means including:
    </p>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>System Data:</strong> Technical data about your computer or
        device like your IP address, device type, operating system, browser,
        and related system data.
      </li>
      <li>
        <strong>Usage Data:</strong> Usage statistics about your
        interactions with the Services, including inspections accessed, time
        spent on modules, features used, your search queries, click data,
        date and time, and other usage information.
      </li>
      <li>
        <strong>Approximate Geographic Data:</strong> An approximate
        geographic location, including information like country, city, and
        geographic coordinates, calculated based on your IP address.
      </li>
    </ul>

    <h2 id="howWe" className="text-xl font-semibold mb-2">
      2. How We Get Data About You
    </h2>
    <p className="mb-4">
      We use tools like cookies, web beacons, and similar tracking
      technologies to gather the data listed above. Some of these tools
      allow you to opt out of data collection.
    </p>

    <h3 className="text-lg font-semibold mb-2">
      2.1 Cookies and Data Collection Tools
    </h3>
    <p className="mb-4">
      We use cookies, which are small text files stored by your browser, to
      collect, store, and share data about your activities across websites,
      including on iCheck. They allow us to remember things about your
      visits, like your preferred language, and to make the platform easier
      to use.
    </p>

    <h3 className="text-lg font-semibold mb-2">
      2.2 Why We Use Data Collection Tools
    </h3>
    <p className="mb-4">
      iCheck uses the following types of Data Collection Tools for the
      purposes described:
    </p>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>Strictly Necessary:</strong> To enable core access, login,
        and secure the system.
      </li>
      <li>
        <strong>Functional:</strong> To remember data about your browser and
        preferences, and customize the platform experience.
      </li>
      <li>
        <strong>Performance:</strong> To measure and improve inspection
        workflows and reporting performance.
      </li>
      <li>
        <strong>Analytics & Reporting:</strong> To understand platform usage
        and enhance compliance features.
      </li>
      <li>
        <strong>Social / Collaboration:</strong> To support features like
        sharing inspection results or collaborating with teams.
      </li>
    </ul>

    <h2 id="weUse" className="text-xl font-semibold mb-2">
      3. What We Use Your Data For
    </h2>
    <p className="mb-4">
      We use your data to provide inspection and compliance management
      features, communicate with you, troubleshoot issues, secure against
      fraud and abuse, improve and update our Services, analyse how people
      use iCheck, generate compliance reports, and as required by law or
      necessary for safety and integrity.
    </p>

    <h2 id="weShare" className="text-xl font-semibold mb-2">
      4. Who We Share Your Data With
    </h2>
    <p className="mb-4">
      We share certain data with service providers who help us operate
      iCheck, analytics partners, and your organization administrators. We
      may also share your data as needed for security, legal compliance, or
      as part of corporate restructuring. Data may also be aggregated or
      anonymized before being shared.
    </p>

    <h2 id="security" className="text-xl font-semibold mb-2">
      5. Security
    </h2>
    <p className="mb-4">
      We implement appropriate security based on the type and sensitivity of
      data being stored. However, as with any internet-enabled system, there
      is always some risk of unauthorized access. We encourage you to
      protect your login credentials and notify us if you suspect
      unauthorized access.
    </p>

    <h2 id="yourRights" className="text-xl font-semibold mb-2">
      6. Your Rights
    </h2>
    <p className="mb-4">
      You have rights around the use of your data, including the ability to
      opt out of promotional communications, restrict cookies, or request
      deletion of your account data. You can update or terminate your
      account from within the platform or by contacting us directly.
    </p>

    <h3 className="text-lg font-semibold mb-2">
      6.1 Accessing, Updating, and Deleting Your Personal Data
    </h3>
    <ul className="list-disc pl-6 mb-4">
      <li>
        To update data you provide directly, log into your account and
        update your profile.
      </li>
      <li>
        To terminate your account, follow the steps in your profile
        settings.
      </li>
      <li>
        If you face issues terminating your account, please contact our
        Support Team.
      </li>
    </ul>

    <h3 className="text-lg font-semibold mb-2">
      6.2 Your Choices About the Use of Your Data
    </h3>
    <p className="mb-4">
      You can choose not to provide certain data, but this may limit access
      to some iCheck features. You can also unsubscribe from promotional
      communications via in-app preferences or unsubscribe links in emails.
    </p>

    <h2 id="jurisdiction" className="text-xl font-semibold mb-2">
      7. Jurisdiction-Specific Rules
    </h2>
    <p className="mb-4">
      If you are located in India, you have specific rights related to
      accessing, updating, and deleting your data, and to know with whom
      your data is shared. Users outside India should note that iCheck data
      may be transferred and stored in India or other countries.
    </p>

    <h2 id="updates" className="text-xl font-semibold mb-2">
      8. Updates & Contact Info
    </h2>
    <p className="mb-4">
      When we make a material change to this policy, we’ll notify users via
      email, in-product notice, or another mechanism required by law.
      Changes become effective the day they’re posted. Please contact us via
      email or postal mail with any questions, concerns, or disputes
      regarding this Privacy Policy.
    </p>
  </>
);

const ChineseContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">隐私政策</h1>
    <div className="rounded-lg border border-border bg-background/40 p-5 sm:p-6 mb-6">
      <h2 className="text-lg sm:text-xl font-semibold mb-4">关键信息</h2>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 sm:gap-y-3 text-sm sm:text-base">
        <dt className="font-medium">应用名称:</dt>
        <dd>iCheck - Inspection Hub</dd>

        <dt className="font-medium">开发者:</dt>
        <dd>Anuj Mengaji</dd>

        <dt className="font-medium">联系邮箱:</dt>
        <dd>
          <a href="mailto:support@elementree.co.in" className="text-primary underline">
            support@elementree.co.in
          </a>
        </dd>

        <dt className="font-medium">生效日期:</dt>
        <dd>2026 年 5 月 14 日</dd>

        <dt className="font-medium">最后更新:</dt>
        <dd>2026 年 5 月 14 日</dd>
      </dl>
    </div>
    <p className="mb-4">
      感谢您使用 iCheck - Inspection Hub——由 Anuj Mengaji 提供的合规与检查管理系统。我们("iCheck"、"我们")尊重您的隐私,并希望您了解我们如何收集、使用和共享有关您的数据。本隐私政策涵盖我们的数据收集做法,并说明您对其个人数据所享有的权利。
    </p>
    <p className="mb-4">
      除非我们链接至其他政策或另有说明,否则本隐私政策适用于您访问或使用 iCheck 网站、移动应用程序、API 或相关服务(以下简称"服务")时。
    </p>
    <p className="mb-4">
      使用本服务即表示您同意本隐私政策的条款。如果您不同意本隐私政策或任何其他管辖您使用本服务的协议,则不应使用本服务。
    </p>

    <h2 className="text-xl font-semibold mb-2">目录</h2>
    <ul className=" pl-6 mb-4">
      <li>
        <a href="#whatData">1. 我们收集哪些数据</a>
      </li>
      <li>
        <a href="#howWe">2. 我们如何获取您的数据</a>
      </li>
      <li>
        <a href="#weUse">3. 我们如何使用您的数据</a>
      </li>
      <li>
        <a href="#weShare">4. 我们与谁共享您的数据</a>
      </li>
      <li>
        <a href="#security">5. 安全</a>
      </li>
      <li>
        <a href="#yourRights">6. 您的权利</a>
      </li>
      <li>
        <a href="#jurisdiction">7. 特定司法管辖区规则</a>
      </li>
      <li>
        <a href="#updates">8. 更新及联系信息</a>
      </li>
    </ul>

    <h2 id="whatData" className="text-xl font-semibold">
      1. 我们收集哪些数据
    </h2>
    <p className="mb-4">
      我们直接从您处收集某些数据,例如您自己输入的信息、您提交的检查数据,以及您与 iCheck 连接的第三方平台上的信息。我们还自动收集一些数据,例如有关您设备的信息以及您与我们服务交互或使用所花费的时间。
    </p>

    <h3 className="text-lg font-semibold mb-2">1.1 您提供给我们的数据</h3>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>账户数据:</strong>{" "}
        为创建用户账户,我们会收集并存储您的电子邮件地址、密码和账户设置。当您使用平台上的某些功能时,系统可能会提示您提交其他信息,包括您的姓名、职业和电话号码。
      </li>
      <li>
        <strong>个人资料数据:</strong>{" "}
        您也可以选择提供个人资料信息,例如照片、职务、简介、语言或其他详细信息。您的个人资料数据可能可由贵公司管理员或相关利益相关方查看。
      </li>
      <li>
        <strong>检查与合规数据:</strong>{" "}
        当您记录检查结果、附加证据或提交合规报告时,我们会将这些数据作为您账户历史的一部分进行收集和存储。
      </li>
      <li>
        <strong>支付数据:</strong>{" "}
        如果您订阅或购买,我们将根据需要收集账单详细信息以处理您的订单。
      </li>
      <li>
        <strong>通信和支持:</strong>{" "}
        如果您联系我们寻求支持或报告问题,我们会收集并存储您的联系信息、消息和其他与您相关的详细信息。
      </li>
    </ul>

    <h3 className="text-lg font-semibold mb-2">1.2 我们通过自动方式收集的数据</h3>
    <p>当您访问本服务时,我们会通过自动方式收集某些数据,包括:</p>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>系统数据:</strong>{" "}
        有关您计算机或设备的技术数据,例如您的 IP 地址、设备类型、操作系统、浏览器和相关系统数据。
      </li>
      <li>
        <strong>使用数据:</strong>{" "}
        有关您与服务交互的使用统计信息,包括访问的检查、在模块上花费的时间、使用的功能、搜索查询、点击数据、日期和时间以及其他使用信息。
      </li>
      <li>
        <strong>大致地理数据:</strong>{" "}
        根据您的 IP 地址计算得出的大致地理位置,包括国家/地区、城市和地理坐标等信息。
      </li>
    </ul>

    <h2 id="howWe" className="text-xl font-semibold mb-2">
      2. 我们如何获取您的数据
    </h2>
    <p className="mb-4">
      我们使用 Cookie、网络信标和类似跟踪技术等工具来收集上述数据。其中一些工具允许您选择退出数据收集。
    </p>

    <h3 className="text-lg font-semibold mb-2">2.1 Cookie 和数据收集工具</h3>
    <p className="mb-4">
      我们使用 Cookie(由您的浏览器存储的小型文本文件)来收集、存储和共享您在各网站(包括 iCheck)上的活动数据。它们使我们能够记住您的访问详情(例如您的首选语言),并使平台更易于使用。
    </p>

    <h3 className="text-lg font-semibold mb-2">2.2 我们为何使用数据收集工具</h3>
    <p className="mb-4">iCheck 出于以下目的使用以下类型的数据收集工具:</p>
    <ul className="list-disc pl-6 mb-4">
      <li>
        <strong>严格必要:</strong> 用于实现核心访问、登录并保护系统安全。
      </li>
      <li>
        <strong>功能性:</strong>{" "}
        用于记住有关您浏览器和偏好的数据,并自定义平台体验。
      </li>
      <li>
        <strong>性能:</strong> 用于衡量和改进检查工作流和报告性能。
      </li>
      <li>
        <strong>分析与报告:</strong> 用于了解平台使用情况并增强合规功能。
      </li>
      <li>
        <strong>社交/协作:</strong> 用于支持共享检查结果或与团队协作等功能。
      </li>
    </ul>

    <h2 id="weUse" className="text-xl font-semibold mb-2">
      3. 我们如何使用您的数据
    </h2>
    <p className="mb-4">
      我们使用您的数据来提供检查和合规管理功能、与您通信、解决问题、防范欺诈和滥用、改进和更新我们的服务、分析人们如何使用 iCheck、生成合规报告,以及根据法律要求或出于安全与完整性需要使用。
    </p>

    <h2 id="weShare" className="text-xl font-semibold mb-2">
      4. 我们与谁共享您的数据
    </h2>
    <p className="mb-4">
      我们与协助我们运营 iCheck 的服务提供商、分析合作伙伴以及您组织的管理员共享某些数据。我们也可能根据安全、法律合规或作为公司重组的一部分而共享您的数据。数据在共享前也可能被汇总或匿名化。
    </p>

    <h2 id="security" className="text-xl font-semibold mb-2">
      5. 安全
    </h2>
    <p className="mb-4">
      我们根据所存储数据的类型和敏感度实施适当的安全措施。但是,与任何联网系统一样,始终存在未授权访问的风险。我们鼓励您保护您的登录凭据,并在怀疑存在未授权访问时通知我们。
    </p>

    <h2 id="yourRights" className="text-xl font-semibold mb-2">
      6. 您的权利
    </h2>
    <p className="mb-4">
      您对数据的使用享有相关权利,包括选择退出促销通讯、限制 Cookie 或请求删除您的账户数据。您可以在平台内更新或终止您的账户,也可以直接联系我们。
    </p>

    <h3 className="text-lg font-semibold mb-2">6.1 访问、更新和删除您的个人数据</h3>
    <ul className="list-disc pl-6 mb-4">
      <li>要更新您直接提供的数据,请登录您的账户并更新您的个人资料。</li>
      <li>要终止您的账户,请按照您的个人资料设置中的步骤进行操作。</li>
      <li>如果您在终止账户时遇到问题,请联系我们的支持团队。</li>
    </ul>

    <h3 className="text-lg font-semibold mb-2">6.2 关于使用您的数据的选择</h3>
    <p className="mb-4">
      您可以选择不提供某些数据,但这可能会限制对某些 iCheck 功能的访问。您还可以通过应用内偏好设置或电子邮件中的退订链接,取消订阅促销通讯。
    </p>

    <h2 id="jurisdiction" className="text-xl font-semibold mb-2">
      7. 特定司法管辖区规则
    </h2>
    <p className="mb-4">
      如果您位于印度,您拥有与访问、更新和删除您的数据,以及了解您的数据与谁共享相关的特定权利。印度以外的用户应注意,iCheck 数据可能会被传输并存储在印度或其他国家/地区。
    </p>

    <h2 id="updates" className="text-xl font-semibold mb-2">
      8. 更新及联系信息
    </h2>
    <p className="mb-4">
      当我们对本政策进行重大变更时,我们将通过电子邮件、产品内通知或法律要求的其他方式通知用户。变更自发布之日起生效。如对本隐私政策有任何疑问、疑虑或争议,请通过电子邮件或邮政信件与我们联系。
    </p>
  </>
);

export default PrivacyPolicyTab;
